/**
 * Conversations at the project's home (docs/plans/2026-10-project-person-machine-platform.md,
 * D215 rounds 3 and 4): a person's Chat and Research in a project, theirs on every machine and
 * private until shared; and Team Work, the project's, which every member reads. One record shape,
 * one message shape, one renderer on the workbench, the console and the phone. AIOE's own
 * contract; the workbench copies it into `shared/schemas/conversations.ts`. Records are project
 * records (projectRecords.ts): If-Match/412, ids the workbench's, append-only messages.
 *
 *   The person's (scope person; only that person, through their sign-in, their desktop's token, or a node naming them):
 *   GET    /projects/v1/:id/me/conversations?kind=chat|research&since=<seq>&limit=   { seq, conversations[] } (no messages)
 *   PUT    /projects/v1/:id/me/conversations/:cid              create or replace the record; If-Match
 *   GET    /projects/v1/:id/me/conversations/:cid
 *   PATCH  /projects/v1/:id/me/conversations/:cid              { title?, pinned?, archived?, label? }; If-Match
 *   DELETE /projects/v1/:id/me/conversations/:cid              for good, for everyone it was shared with (copies made by `share` stay)
 *   POST   /projects/v1/:id/me/conversations/:cid/messages     { messages[] } append; server numbers; idempotent by message id
 *   GET    /projects/v1/:id/me/conversations/:cid/messages?after=<seq>&limit=500
 *   POST   /projects/v1/:id/me/conversations/:cid/share        { what: "answer" | "conversation", messageId?, note? } → 201 the shared copy
 *                                                              (`conversation` is superseded by the audience below; `answer` stays)
 *   PUT    /projects/v1/:id/me/conversations/:cid/audience     { audience } the owner only (403 owner_only); 400 not_a_member { ids }; the owner is always in `members`
 *   GET|PUT /projects/v1/:id/me/preferences                     one small document; If-Match
 *
 *   The project's (scope project, kind teamwork; members read, contributors and above write):
 *   GET    /projects/v1/:id/teamwork/conversations?since=&limit=
 *   PUT|GET|PATCH /projects/v1/:id/teamwork/conversations/:cid
 *   POST|GET /projects/v1/:id/teamwork/conversations/:cid/messages
 *   PATCH  /projects/v1/:id/teamwork/conversations/:cid        { steerers } the conversation's owner only (403 owner_only); 400 not_a_member { ids }
 *   GET    /projects/v1/:id/shared?since=&limit=                members: the copies people shared (kind shared-conversation)
 *
 *   Shared live (D276; members): a Chat or Research conversation its owner shared with the project or with chosen members
 *   GET    /projects/v1/:id/conversations/shared?kind=chat|research&since=&limit=   { seq, conversations[] }, each with `owner`
 *   GET    /projects/v1/:id/conversations/:principal/:cid[/messages?after=&limit=]  404 not_found unless shared with the caller or theirs (never 403)
 *
 * A message over MESSAGE_MAX_BYTES loses its `blob` blocks and keeps the rest. A conversation may
 * carry `label` (the highest its session held); a share copies it, and the project copy is never
 * labelled lower. Words for people: conversation, shared with the project, the answer.
 *
 * People in Team Work (workbench 0.89.20; teamworkMentions.ts, teamDelegation.ts, availability.ts):
 *  - A Team Work record carries `owner` (whoever started it; set by AIOE, kept on every save) and `steerers` (the
 *    people besides the owner who may steer it; changed only by the owner, with PATCH).
 *  - Every Team Work message carries `author { kind, id, name }`, set by AIOE from the verified caller and never from
 *    the body: a sign-in is always the person; a desktop or a node posting `role: "user"` is the person it speaks for;
 *    `role: "assistant"` is an agent (its id from the body's `author.id`, else `x-aioe-actor`); `role: "system"` is the
 *    machine. An `intent` says what the message is for:
 *      steer   the conversation's owner or a steerer only: 403 `not_a_steerer { who }`;
 *      reply   any member who may write (and the reading of a message without an intent);
 *      answer  with `questionId`: the person the question names, or whoever covers them (`delegation.cover` of the
 *              conversation's team: `always`, or `away` while they are away): 403 `not_asked { who }`; an answer by a
 *              cover carries `answeredFor { id, name }` (set by AIOE). 400 `unknown_question` when no message asked it.
 *    `mentions` and `asks` (teamworkMentions.ts) name members of the project only: 400 `not_a_member { ids }`.
 *  - AIOE files the hub records once, from the verified message, whoever posted it: a `mention` (fyi) for each person
 *    mentioned, and a `question` (needs-you) for the person asked (and whoever covers them now), done when answered
 *    or when the conversation is archived or removed. Every new message is the change hint `teamwork:<projectId>`.
 */
import { z } from "zod";
import { recordLabelSchema } from "./projectRecords.js";
import { teamworkAskSchema, teamworkMentionsSchema } from "./teamworkMentions.js";
export const CONVERSATIONS_SCHEMA_VERSION = 1;
export const MESSAGE_MAX_BYTES = 200_000;
export const MESSAGES_PER_CONVERSATION_MAX = 5_000;
export const CONVERSATION_KINDS = ["chat", "research", "teamwork"];
export const conversationKindSchema = z.enum(CONVERSATION_KINDS);
const id = z.string().min(1).max(200);
/** A file the conversation made or holds: a reference; the bytes stay with the workbench this round. */
export const artifactRefSchema = z.looseObject({ id: id.optional(), name: z.string().max(300), mimeType: z.string().max(200).optional(), sha256: z.string().max(100).optional(), size: z.number().int().nonnegative().optional(), path: z.string().max(1000).optional() });
/** The record, as the workbench keeps it (src/facilitator/types.ts); checked fields only, the rest carried. */
export const conversationPutSchema = z.looseObject({
    id,
    kind: conversationKindSchema,
    title: z.string().max(300).default(""),
    mode: z.string().max(50).optional(),
    createdAt: z.string().max(50),
    updatedAt: z.string().max(50).optional(),
    teamId: z.string().max(200).optional(),
    specialistId: z.string().max(200).optional(),
    briefId: z.string().max(200).optional(),
    model: z.string().max(200).optional(),
    pinned: z.boolean().optional(),
    archived: z.boolean().optional(),
    groupId: z.string().max(200).optional(),
    label: recordLabelSchema.optional(),
    artifacts: z.array(artifactRefSchema).max(200).optional(),
    sessionArtifacts: z.array(artifactRefSchema).max(200).optional(),
    /** Team Work: the person who started it. Set by AIOE; a body's is ignored. */
    owner: z.object({ id: z.string().min(1).max(200), name: z.string().max(200) }).optional(),
    /** Team Work: who besides the owner may steer it. Taken from the body only on create; afterwards PATCH by the owner. */
    steerers: z.array(z.string().min(1).max(200)).max(50).optional(),
});
/** Who may read a person's conversation: only them (default), the whole project, or chosen members. */
export const conversationAudienceSchema = z.object({
    kind: z.enum(["person", "project", "members"]),
    members: z.array(z.object({ id: z.string().min(1).max(200), name: z.string().min(1).max(200) })).max(50).optional(),
});
export const conversationAudiencePutSchema = z.object({ audience: conversationAudienceSchema });
/** The audience in words: "Only you", "Shared with the project", "Shared with Ana and Ben". `me` is the caller's id, shown as "you". */
export function audienceWords(a, me) {
    if (!a || a.kind === "person")
        return "Only you";
    if (a.kind === "project")
        return "Shared with the project";
    const names = (a.members ?? []).map((m) => (me && m.id === me ? "you" : m.name));
    if (!names.length)
        return "Only you";
    return `Shared with ${names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`}`;
}
export const conversationPatchSchema = z.object({
    title: z.string().max(300).optional(),
    pinned: z.boolean().optional(),
    archived: z.boolean().optional(),
    label: recordLabelSchema.optional(),
    /** Team Work: the people who may steer besides the owner (the owner only). */
    steerers: z.array(z.string().min(1).max(200)).max(50).optional(),
});
/** What a Team Work message is for (see the header). */
export const TEAMWORK_INTENTS = ["steer", "reply", "answer"];
export const teamworkIntentSchema = z.enum(TEAMWORK_INTENTS);
export const MESSAGE_AUTHOR_KINDS = ["person", "agent", "system"];
export const messageRoleSchema = z.enum(["user", "assistant", "system"]);
export const conversationMessageSchema = z.looseObject({
    id,
    role: messageRoleSchema,
    text: z.string().max(400_000).default(""),
    createdAt: z.string().max(50),
    thought: z.string().max(400_000).optional(),
    author: z.looseObject({ kind: z.enum(MESSAGE_AUTHOR_KINDS).optional(), id: z.string().max(200).optional(), name: z.string().max(200), avatar: z.string().max(2000).optional(), color: z.string().max(50).optional() }).optional(),
    attachments: z.array(z.looseObject({ kind: z.enum(["image", "file"]), name: z.string().max(300), mimeType: z.string().max(200), sha256: z.string().max(100).optional(), size: z.number().int().nonnegative().optional() })).max(50).optional(),
    blocks: z.array(z.looseObject({ kind: z.string().max(50) })).max(200).optional(),
    ops: z.array(z.looseObject({})).max(2000).optional(),
    endedAt: z.string().max(50).optional(),
    stopped: z.boolean().optional(),
    marker: z.string().max(100).optional(),
    retried: z.boolean().optional(),
    /** Team Work (see the header). */
    intent: teamworkIntentSchema.optional(),
    questionId: z.string().min(1).max(200).optional(),
    /** Set by AIOE on an answer by someone covering the person asked. */
    answeredFor: z.object({ id: z.string(), name: z.string() }).optional(),
    mentions: teamworkMentionsSchema.optional(),
    asks: teamworkAskSchema.optional(),
});
export const messagesPostSchema = z.object({ messages: z.array(conversationMessageSchema).min(1).max(500) });
/**
 * The hub records AIOE files from a Team Work message. A mention: `kind: "mention"`, fyi, one per person and message.
 * A question: `kind: "question"`, needs-you, one per question; its actions answer in place (`TEAMWORK_ANSWER_RPC`
 * through `POST …/notifications/:nid/act`, body `{ text }`, or an option's own action) as the person acting, and open
 * the conversation (`OPEN_VIEW_ACTION`, a client's own navigation, never a call).
 */
export const TEAMWORK_ANSWER_RPC = "aioe.teamwork.answer";
export const OPEN_VIEW_ACTION = "open";
export const teamworkMentionKey = (conversationId, messageId, subjectId) => `mention:${conversationId}:${messageId}:${subjectId}`;
export const teamworkQuestionKey = (questionId) => `question:${questionId}`;
export const shareRequestSchema = z.object({
    what: z.enum(["answer", "conversation"]),
    /** With `answer`: the message shared; the newest assistant message when left out. */
    messageId: id.optional(),
    note: z.string().max(2000).optional(),
});
export const preferencesPutSchema = z.object({ preferences: z.record(z.string(), z.unknown()) });
/** A message that would exceed the limit loses its blob blocks first, then its ops; what is left is kept. */
export function fitMessage(m) {
    const size = (x) => new TextEncoder().encode(JSON.stringify(x)).length;
    if (size(m) <= MESSAGE_MAX_BYTES)
        return { message: m, trimmed: false };
    const withoutBlobs = { ...m, blocks: (m.blocks ?? []).map((b) => ("blob" in b ? { ...b, blob: undefined, trimmed: true } : b)) };
    if (size(withoutBlobs) <= MESSAGE_MAX_BYTES)
        return { message: withoutBlobs, trimmed: true };
    const { ops: _ops, ...withoutOps } = withoutBlobs;
    if (size(withoutOps) <= MESSAGE_MAX_BYTES)
        return { message: { ...withoutOps, trimmed: true }, trimmed: true };
    return { message: { ...withoutOps, text: withoutOps.text.slice(0, MESSAGE_MAX_BYTES / 2), thought: undefined, trimmed: true }, trimmed: true };
}
//# sourceMappingURL=conversations.js.map